import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Stack, LinearProgress, Chip, Paper, TextField, MenuItem, Select, FormControl, InputLabel, Autocomplete, CircularProgress, Tooltip, IconButton, Divider, Button } from '@mui/material';
import { CalendarMonth, InfoOutlined, AccessTime, History, ArrowBack, Save, Close } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData, putData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { formatDate } from '../../../utils/dateFormat';
import { getStatusColor, priorityColors } from '../../../utils/statusColor';

const EpicDetailsPage = forwardRef(({ epicId, isEditing, onEditSuccess, onClose }, ref) => {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const orgId = userData?.organization_id;

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({});
    const [projects, setProjects] = useState([]);
    const [productOwners, setProductOwners] = useState([]);
    const [teamLeads, setTeamLeads] = useState([]);
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [dateHistory, setDateHistory] = useState([]);
    const [isEditingLocal, setIsEditingLocal] = useState(false);
    const [searchParams] = useSearchParams();
    const effectiveEpicId = epicId || searchParams.get('epicId');

    useImperativeHandle(ref, () => ({
        handleSave
    }));

    // Calculate Financial Year Boundaries (April to March)
    const financialYearRange = useMemo(() => {
        const today = dayjs();
        const currentMonth = today.month();
        let startYear = today.year();

        if (currentMonth < 3) {
            startYear = today.year() - 1;
        }

        return {
            start: dayjs(`${startYear}-04-01`),
            end: dayjs(`${startYear + 1}-03-31`)
        };
    }, []);

    const fetchDetails = useCallback(async () => {
        if (!effectiveEpicId) return;
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchEpicById(effectiveEpicId));
            if (res.status === "SUCCESS") {
                const data = res.message.epic;
                const history = res.message.date_history || []; // Get history
                setDetails(data);
                setDateHistory(history); // Set history
                setFormData({
                    title: data.title || "",
                    project_id: data.project_id || "",
                    product_owner: data.product_owner_id || "",
                    team_lead: data.team_lead_id || "",
                    description: data.description || "",
                    status: data.status || "Todo",
                    priority: data.priority || "Medium",
                    start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : "",
                    end_date: data.end_date ? dayjs(data.end_date).format('YYYY-MM-DD') : "",
                    change_reason: ""
                });
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [effectiveEpicId]);

    const fetchProjects = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
            if (res.status === "SUCCESS") {
                const projectData = res.message.data || res.message || [];
                setProjects(Array.isArray(projectData) ? projectData : []);
            }
        } catch (error) {
            setProjects([]);
            return null;
        }
    }, [userId]);

    const fetchTeamLeads = useCallback(async () => {
        if (!orgId) return;
        setFetchingUsers(true);
        try {
            const res = await getData(API_ENDPOINTS.fetchTeamLeadsForOrganization(orgId));
            if (res.status === "SUCCESS") {
                const leads = res.message.users;
                setTeamLeads(Array.isArray(leads) ? leads : []);
            }
        } catch (error) {
            setTeamLeads([]); // Move this above return
            return null;
        } finally {
            setFetchingUsers(false);
        }
    }, [orgId]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);
    useEffect(() => { fetchProjects(); }, [fetchProjects]);
    useEffect(() => {
        if (isEditing) fetchTeamLeads();
    }, [isEditing, fetchTeamLeads]);

    useEffect(() => {
        const fetchProductOwners = async () => {
            if (!formData.project_id) {
                setProductOwners([]);
                return;
            }
            setFetchingUsers(true);
            try {
                const res = await getData(API_ENDPOINTS.fetchProductOwnersForProject(formData.project_id));
                if (res.status === "SUCCESS") {
                    const owners = res.result;
                    setProductOwners(Array.isArray(owners) ? owners : []);
                }
            } catch (error) {
                setProductOwners([]);
            } finally {
                setFetchingUsers(false);
            }
        };
        fetchProductOwners();
    }, [formData.project_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name, newValue) => {
        const dateString = newValue ? newValue.format('YYYY-MM-DD') : "";
        setFormData(prev => {
            const newData = { ...prev, [name]: dateString };
            if (name === 'start_date' && prev.end_date && dayjs(dateString).isAfter(dayjs(prev.end_date))) {
                newData.end_date = "";
            }
            return newData;
        });
    };

    const isDateChanged = useMemo(() => {
        if (!details) return false;

        // Helper to normalize dates to YYYY-MM-DD for strict comparison
        const normalize = (date) => (date ? dayjs(date).format('YYYY-MM-DD') : "");

        const originalStart = normalize(details.start_date);
        const originalEnd = normalize(details.end_date);

        const currentStart = normalize(formData.start_date);
        const currentEnd = normalize(formData.end_date);

        return currentStart !== originalStart || currentEnd !== originalEnd;
    }, [formData.start_date, formData.end_date, details]);

    // Validation Logic
    const isDescriptionMissing = !formData.description || formData.description.trim() === "";
    const isReasonMissing = isDateChanged && (!formData.change_reason || formData.change_reason.trim() === "");
    const isSaveDisabled = saving || isDescriptionMissing || isReasonMissing;

    const handleSave = async () => {
        if (isSaveDisabled) return;
        setSaving(true);
        try {
            const payload = {
                title: formData.title,
                project_id: formData.project_id,
                product_owner_id: formData.product_owner,
                team_lead_id: formData.team_lead,
                description: formData.description,
                status: formData.status,
                priority: formData.priority,
                start_date: formData.start_date,
                end_date: formData.end_date,
                reason: isDateChanged ? formData.change_reason : undefined
            };
            const res = await putData(API_ENDPOINTS.updateEpic(epicId), payload);
            if (res.status === "SUCCESS") {
                await Promise.all([fetchDetails(), fetchProjects(), fetchTeamLeads()]);
                if (onEditSuccess) onEditSuccess();
            }
        } catch (error) {
            return null;
        } finally {
            setSaving(false);
        }
    };

    const getProjectTitle = (id) => projects.find(p => p.id === id)?.project_title || "No project assigned";

    const getTeamMemberDisplayName = (member) => {
        if (!member) return '';
        if (typeof member === 'string') return member;
        if (member.fullName) return member.fullName;
        if (member.first_name && member.last_name) return `${member.first_name} ${member.last_name}`;
        return member.name || member.email || '';
    };

    const activeProjectDates = useMemo(() => {
        const project = projects.find(p => p.id === formData.project_id);
        if (!project) return null;
        return {
            start: dayjs(project.start_date),
            end: dayjs(project.end_date)
        };
    }, [projects, formData.project_id]);

    const today = dayjs().startOf('day');

    useEffect(() => { fetchDetails(); }, [fetchDetails]);
    const isEditable = isEditing !== undefined ? isEditing : isEditingLocal;

    if (loading) return (
        <Box sx={{ display: 'flex', minHeight: '500px', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        </Box>
    );

    if (!details) return <Box p={4} textAlign="center">No data found.</Box>;

    const statusStyle = getStatusColor(details.status);
    const priorityStyle = priorityColors[details.priority] || priorityColors.Medium;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: 1 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">{details.title || 'Epic Details'}</Typography>
                        <Typography variant="caption" color="text.secondary">Epic Id: {details.epic_code}</Typography>
                    </Box>
                    {isEditable && (
                        <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                            <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving} sx={{ bgcolor: '#10b981', textTransform: 'none' }}>Save Changes</Button>
                            <Button variant="outlined" size="small" startIcon={<Close />} onClick={() => setIsEditingLocal(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                        </Stack>
                    )}
                </Box>
            </Stack>

            <Box sx={{ display: 'flex', minHeight: '500px', bgcolor: '#fff', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', flex: 1 }}>
                    <Box sx={{ flex: 1, p: 3, borderRight: '1px solid #e2e8f0', overflowY: 'auto' }}>
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '0.875rem', letterSpacing: 0.8, display: 'block' }}>Epic Title</Typography>
                            {isEditing ? (
                                <TextField fullWidth name="title" value={formData.title} onChange={handleInputChange} size="small" variant="outlined" sx={{ mt: 0.5, bgcolor: '#fff' }} />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#334155' }}>{formData.title || "Untitled Epic"}</Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '0.875rem', letterSpacing: 0.8, display: 'block' }}>Project</Typography>
                            {isEditing ? (
                                <Autocomplete
                                    options={projects}
                                    getOptionLabel={(option) => option.project_title || ""}
                                    value={projects.find(p => p.id === formData.project_id) || null}
                                    onChange={(event, newValue) => setFormData(prev => ({ ...prev, project_id: newValue ? newValue.id : "", product_owner: "" }))}
                                    renderInput={(params) => <TextField {...params} size="small" variant="outlined" sx={{ mt: 0.5, bgcolor: '#fff' }} placeholder="Select Project" />}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#334155' }}>{getProjectTitle(formData.project_id)}</Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '0.875rem', letterSpacing: 0.8, display: 'block' }}>Product Owner</Typography>
                            {isEditing ? (
                                <Autocomplete
                                    options={productOwners}
                                    value={productOwners.find(u => u.id === formData.product_owner) || null}
                                    onChange={(e, value) => setFormData(prev => ({ ...prev, product_owner: value?.id || "" }))}
                                    getOptionLabel={getTeamMemberDisplayName}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={fetchingUsers}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select Product Owner" fullWidth InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (<>{fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                                        }} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                    )}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#334155' }}>{details.product_owner_name || "No owner assigned."}</Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '0.875rem', letterSpacing: 0.8, display: 'block' }}>Team Lead</Typography>
                            {isEditing ? (
                                <Autocomplete
                                    options={teamLeads}
                                    value={teamLeads.find(u => u.id === formData.team_lead) || null}
                                    onChange={(e, value) => setFormData(prev => ({ ...prev, team_lead: value?.id || "" }))}
                                    getOptionLabel={getTeamMemberDisplayName}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={fetchingUsers}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select Team Lead" fullWidth InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (<>{fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                                        }} sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                    )}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#334155' }}>{details.team_lead_name || "No lead assigned."}</Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="overline" color="rgb(98 116 142)" fontWeight="700" sx={{ fontSize: '0.875rem', letterSpacing: 0.8, display: 'block' }}>
                                Description *
                            </Typography>
                            {isEditing ? (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    size="small"
                                    variant="outlined"
                                    error={isDescriptionMissing}
                                    helperText={isDescriptionMissing ? "Description is mandatory" : ""}
                                    sx={{ mt: 0.5, bgcolor: '#fff' }}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.3 }}>{formData.description || "No description provided."}</Typography>
                            )}
                        </Box>

                        {/* MANDATORY REASON FOR CHANGE BOX */}
                        {isEditing && isDateChanged && (
                            <Box>
                                <Typography variant="subtitle2" color="rgb(98 116 142)" fontWeight="700">
                                    Reason for Date Change *
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    name="change_reason"
                                    placeholder="Explain why the timeline has changed..."
                                    value={formData.change_reason}
                                    onChange={handleInputChange}
                                    size="small"
                                    variant="outlined"
                                    error={isReasonMissing}
                                    helperText={isReasonMissing ? "Reason is required when dates are modified" : ""}
                                    sx={{ bgcolor: '#fff', mt: 1 }}
                                />
                            </Box>
                        )}

                        {/* Add this after the "Last Updated" Paper component */}
                        {dateHistory.length > 0 && (
                            <Paper variant="outlined" sx={{ mt: 3, borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <History sx={{ fontSize: 18, color: '#64748b' }} />
                                    <Typography variant="overline" fontWeight="700" sx={{ color: '#475569' }}>Timeline History</Typography>
                                </Box>

                                <Box sx={{ maxHeight: '300px', overflowY: 'auto', p: 2 }}>
                                    <Stack spacing={2}>
                                        {dateHistory.map((item, index) => (
                                            <Box key={index} sx={{ position: 'relative', pl: 2, borderLeft: '2px solid #e2e8f0' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="caption" color="textSecondary" fontWeight="600" sx={{ display: 'block', mb: 0.5 }}>
                                                        {dayjs(item.createdAt).format('MMM D, YYYY • h:mm A')}
                                                    </Typography>

                                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                                        <Chip
                                                            size="small"
                                                            label={`Start Date: ${formatDate(item.modified_start_date)}`}
                                                            sx={{ fontSize: '10px', height: '20px', bgcolor: '#eef2ff', color: '#6366f1' }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`End Date: ${formatDate(item.modified_end_date)}`}
                                                            sx={{ fontSize: '10px', height: '20px', bgcolor: '#fff1f2', color: '#ef4444' }}
                                                        />
                                                    </Stack>
                                                </Box>

                                                <Typography variant="body2" sx={{
                                                    fontSize: '12px',
                                                    color: '#475569',
                                                    fontStyle: 'italic',
                                                    bgcolor: '#f1f5f9',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {item.reason || 'No reason provided'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    <Box sx={{ width: '320px', p: 3, bgcolor: '#fcfcfd' }}>
                        <Stack direction="column" spacing={2} sx={{ mb: 3 }}>
                            {isEditing ? (
                                <>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select name="status" value={formData.status} label="Status" onChange={handleInputChange}>
                                            <MenuItem value="Todo">To Do</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="On Hold">On Hold</MenuItem>
                                            <MenuItem value="Done">Done</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Priority</InputLabel>
                                        <Select name="priority" value={formData.priority} label="Priority" onChange={handleInputChange}>
                                            <MenuItem value="Low">Low</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        label={details.status || "To Do"}
                                        size="small"
                                        sx={{
                                            width: '120px',
                                            bgcolor: statusStyle.bg,    // Dynamic background
                                            color: statusStyle.color,   // Dynamic text color
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            fontSize: '0.7rem',
                                            border: statusStyle.border || '1px solid #cbd5e1'
                                        }}
                                    />
                                    <Chip
                                        icon={<InfoOutlined style={{ fontSize: 16, color: priorityStyle.color }} />}
                                        label={`Priority: ${details.priority || "Medium"}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            width: '120px',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            bgcolor: priorityStyle.bg,    // Apply the background color
                                            color: priorityStyle.color,
                                            border: priorityStyle.border || '1px solid #cbd5e1'
                                        }}
                                    />
                                </Stack>
                            )}
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Overall Progress</Typography>
                                <Typography variant="h5" fontWeight="800" color="primary">{details.progress_percentage}%</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={Number(details.progress_percentage || 0)} sx={{ mt: 1.5, height: 8, borderRadius: 4, bgcolor: '#e2e8f0' }} />
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" >
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Total Stories</Typography>
                                <Typography variant="h5" fontWeight="800" sx={{ color: '#1C7ED6' }}>{details.total_user_stories}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" >
                                <Typography variant="overline" color="textSecondary" fontWeight="700">Completed</Typography>
                                <Typography variant="h5" fontWeight="800" sx={{ color: '#059669' }}>{details.completed_stories}</Typography>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 3, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="overline" color="textSecondary" fontWeight="700">
                                    Epic Timeline
                                </Typography>
                                {/* Info Icon with Calculation Tooltip */}
                                <Tooltip
                                    title="Allocated hours = (Business days − weekends − holidays) × 8 hrs/day"
                                    arrow
                                    placement="top"
                                >
                                    <InfoOutlined sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
                                </Tooltip>
                            </Stack>

                            <Box sx={{ px: 0.5 }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="textSecondary" display="block" fontWeight="600">START DATE</Typography>
                                        {isEditing ? (
                                            <DatePicker
                                                label="Start Date"
                                                value={formData.start_date ? dayjs(formData.start_date) : null}
                                                onChange={(date) => handleDateChange('start_date', date)}
                                                format="DD/MM/YYYY"
                                                // DISABLE previous dates relative to Today (19/04)
                                                minDate={today}
                                                // CANNOT exceed Project End Date
                                                maxDate={activeProjectDates?.end || financialYearRange.end}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        helperText: activeProjectDates ? `Project Start: ${activeProjectDates.start.format('DD/MM/YYYY')}` : ''
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                                <CalendarMonth sx={{ fontSize: 16, color: '#6366f1' }} />
                                                <Typography variant="body2" fontWeight="700" noWrap>
                                                    {details.start_date ? dayjs(details.start_date).format('MMM D, YYYY') : 'N/A'}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0, textAlign: isEditing ? 'left' : 'right' }}>
                                        <Typography variant="caption" color="textSecondary" display="block" fontWeight="600">DUE DATE</Typography>
                                        {isEditing ? (
                                            <DatePicker
                                                value={formData.end_date ? dayjs(formData.end_date) : null}
                                                onChange={(date) => handleDateChange('end_date', date)}
                                                format='DD/MM/YYYY'
                                                // CANNOT be before Epic Start Date
                                                minDate={formData.start_date ? dayjs(formData.start_date) : today}
                                                // CANNOT exceed Project End Date
                                                maxDate={activeProjectDates?.end || financialYearRange.end}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        helperText: activeProjectDates ? `Project End: ${activeProjectDates.end.format('DD/MM/YYYY')}` : ''
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                                                <Typography variant="body2" fontWeight="700" noWrap>
                                                    {details.end_date ? dayjs(details.end_date).format('MMM D, YYYY') : 'N/A'}
                                                </Typography>
                                                <CalendarMonth sx={{ fontSize: 16, color: '#ef4444' }} />
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>

                                {/* Allocated Hours Display */}
                                <Box sx={{ mt: 2, textAlign: 'center', p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }} >
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                        <AccessTime sx={{ fontSize: 16, color: '#6366f1' }} />
                                        <Typography variant="body2" fontWeight="700" color="textSecondary">
                                            {details.allocated_hours} Allocated Hours
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <Typography variant="overline" color="textSecondary" fontWeight="700">Last updated</Typography>
                            <Typography variant="body2" color="textSecondary">{details.updatedAt ? new Date(details.updatedAt).toLocaleString() : 'Never'}</Typography>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
});

export default EpicDetailsPage;